import { FunctionComponent, h } from 'preact'
import { useEffect, useRef, useState } from 'preact/hooks'
import { useReceiveMessages } from 'scripts/hooks'

import ReplyForm from 'components/business/ReplyForm'
import BaseContext from 'contexts/base-context'
import { patchPublishedEntry, queryCampaignFields } from 'scripts/service'
import {
  CampaignType,
  DescriptionField,
  Field,
  FieldEntry,
  FormFieldType,
  RadioSelectField,
  TextArea,
  TriggerEvent,
} from 'scripts/type'
import { getStorageFormSetting, getStorageKey, getUrlParam, postMessage, Storage } from 'scripts/utils'
import { WIDGET_AUTO_CLOSE_DURATION } from 'scripts/constants'
import { WidgetWidth } from 'components/business/Preview/type'

export type Props = {
  token: string
  isUrlVisit?: boolean
}

const Reply: FunctionComponent<Props> = (props) => {
  const { token, isUrlVisit } = props
  Storage.setItem(getStorageKey('urlPublishedFormToken', token), token)
  const { parentWindowSize } = useReceiveMessages(token)
  const submitLoadingRef = useRef(false)
  const [currentField, setCurrentField] = useState<Field>()
  const [descriptionField, setDescriptionField] = useState<DescriptionField>()
  const [radioField, setRadioField] = useState<RadioSelectField>()
  const [textareaField, setTextareaField] = useState<TextArea>()
  const width = (getUrlParam('width') || WidgetWidth.LARGE) as WidgetWidth

  const queryFields = () => {
    queryCampaignFields({
      campaignToken: token,
      type: CampaignType.REPLY,
      isUrlVisit,
    }).then((data) => {
      const fields = data.nodes as Field[]
      setDescriptionField(fields.find(({ code }) => code === 'field_1') as DescriptionField)
      setRadioField(fields.find(({ code }) => code === 'field_2') as RadioSelectField)
      setTextareaField(fields.find(({ code }) => code === 'field_3') as TextArea)
      setCurrentField(fields.find(({ code }) => code === 'field_2') as RadioSelectField)
    })
  }

  useEffect(() => {
    postMessage({ name: TriggerEvent.Loaded, data: { campaignToken: token } })
    queryFields()
  }, [])

  const handleFieldChange = (height: number) => {
    if (currentField?.type === FormFieldType.RadioButton) {
      postMessage({ name: TriggerEvent.FieldChange, data: { height: height, campaignToken: token } })
    }
    if (currentField?.type === FormFieldType.EndingField) {
      const publishedEntryId = Storage.getItem(getStorageKey('publishedEntryId', token))
      postMessage({ name: TriggerEvent.Complete, data: { campaignToken: token, entryId: publishedEntryId } })
      setTimeout(() => {
        postMessage({ name: TriggerEvent.Close, data: { campaignToken: token } })
      }, WIDGET_AUTO_CLOSE_DURATION)
    }
  }

  const handleRadioValueChange = async (fieldEntry: FieldEntry) => {
    await doPatchPublishedEntry([fieldEntry]).then(() => {
      setCurrentField(textareaField)
    })
  }

  const handleSubmit = async (fieldEntry: FieldEntry) => {
    if (submitLoadingRef.current) {
      return
    }
    await doPatchPublishedEntry([fieldEntry]).finally(() =>
      postMessage({ name: TriggerEvent.Close, data: { campaignToken: token } })
    )
  }

  const doPatchPublishedEntry = async (fieldsEntry: FieldEntry[]) => {
    try {
      submitLoadingRef.current = true
      await patchPublishedEntry(fieldsEntry, token)
    } finally {
      submitLoadingRef.current = false
    }
  }

  return (
    <BaseContext.Provider
      value={{
        parentWindowSize,
        interactive: true,
        isPreview: false,
        urlToken: token,
        isUrlVisit,
        formSetting: getStorageFormSetting(token),
        width,
      }}
    >
      {currentField && (
        <ReplyForm
          field={currentField}
          descriptionField={descriptionField}
          radioField={radioField}
          textareaField={textareaField}
          onRadioValueChange={handleRadioValueChange}
          onSubmit={handleSubmit}
        />
      )}
    </BaseContext.Provider>
  )
}

export default Reply
